import { useCallback, useMemo, useState } from "react";
import type { Animal } from "../data/animals";
import { drawingsForLevel } from "../data/animals";
import { masterpieces } from "../data/masterpieces";
import { allFlags, FOOTBALL_CUPS, worldCupDone } from "../data/worldcup";
import { rewardTiers } from "../data/rewards";
import { FRIEND_DRAWINGS } from "../data/friends";
import { DrawingThumb } from "./DrawingThumb";
import { useVoiceControl } from "../voice/VoiceProvider";
import { scrollPage } from "../lib/scroll";
import { heardAny } from "../voice/match";
import { useLocale, useT } from "../i18n/LocaleProvider";
import {
  aliasesFor,
  cupTextOf,
  genderOf,
  localizeAnimal,
  rewardTextOf,
} from "../i18n/localize";

interface Props {
  onHome: () => void;
  /** Ids of drawings the child has finished (flags included). */
  completed: Set<string>;
  /** Clear every earned sticker and start the shelf over. */
  onReset: () => void;
  /** Start drawing one of the not-yet-done pictures. */
  onPick: (animal: Animal) => void;
}

// Show the same subjects in the same order across Easy/Medium/Harder so the
// shelves line up (the underlying per-level arrays aren't in the same order).
const SUBJECT_ORDER = drawingsForLevel(5).map((a) => a.name);
const bySubject = (items: Animal[]): Animal[] =>
  [...items].sort((a, b) => {
    const ia = SUBJECT_ORDER.indexOf(a.name);
    const ib = SUBJECT_ORDER.indexOf(b.name);
    return (ia === -1 ? Infinity : ia) - (ib === -1 ? Infinity : ib);
  });

// Every sticker section — the leveled drawings, paintings AND flags. Flags now
// count toward the trophies like any other drawing, so they're a full group.
// `allFlags` already includes the World Cup countries, so they're not a separate
// group (and aren't double-counted in the tally or reward shelf).
type GroupKey =
  | "trophyGroupEasy"
  | "trophyGroupMedium"
  | "trophyGroupHarder"
  | "trophyGroupPaintings"
  | "trophyGroupFlags";
const GROUPS: { title: GroupKey; items: Animal[] }[] = [
  { title: "trophyGroupEasy", items: bySubject(drawingsForLevel(5)) },
  { title: "trophyGroupMedium", items: bySubject(drawingsForLevel(7)) },
  { title: "trophyGroupHarder", items: bySubject(drawingsForLevel(10)) },
  { title: "trophyGroupPaintings", items: masterpieces },
  { title: "trophyGroupFlags", items: allFlags },
];

export function TrophyPage({ onHome, completed, onReset, onPick }: Props) {
  const t = useT();
  const { locale } = useLocale();
  const [confirming, setConfirming] = useState(false);

  // GROUPS is a module constant built straight from the drawing data, so its
  // items never went through `localizeAnimal` — that's why the shelf showed
  // "Dog" under a French heading. Localize for DISPLAY here; `onPick` still
  // receives the untranslated drawing, since the app localizes on render.
  const groups = useMemo(
    () =>
      GROUPS.map((g) => ({
        ...g,
        items: g.items.map((a) => ({ a, view: localizeAnimal(a, locale) })),
        done: completed,
      })),
    [completed, locale],
  );

  const onCommand = useCallback(
    (transcript: string): boolean => {
      // Navigation wins over scrolling, so a "home" grouped with a "down" the
      // child said while browsing isn't swallowed by the scroll command.
      if (heardAny(transcript, [...t.commands.home, ...t.commands.back])) {
        onHome();
        return true;
      }
      if (heardAny(transcript, t.commands.up)) {
        scrollPage(-1);
        return true;
      }
      if (heardAny(transcript, t.commands.down)) {
        scrollPage(1);
        return true;
      }
      // Saying a drawing's name starts it — but only for ones not done yet.
      for (const g of groups) {
        for (const { a, view } of g.items) {
          if (g.done.has(a.id)) continue;
          // Match what the child would SAY: the localized aliases, not the
          // English name (this shelf used to only answer to "dog", never "chien").
          if (heardAny(transcript, aliasesFor(view, locale))) {
            onPick(a);
            return true;
          }
        }
      }
      return false;
    },
    [onHome, onPick, groups, t, locale],
  );
  useVoiceControl(onCommand);

  // Sticker tally across every section (flags included) — and the same totals
  // drive the "animal friend" reward shelf, since flags now count toward it.
  const totalDone = groups.reduce(
    (n, g) => n + g.items.filter(({ a }) => g.done.has(a.id)).length,
    0,
  );
  const totalAll = groups.reduce((n, g) => n + g.items.length, 0);
  const allDone = totalAll > 0 && totalDone === totalAll;

  const rewards = rewardTiers(totalAll);
  const rewardsEarned = rewards.filter((r) => totalDone >= r.need).length;
  const nextReward = rewards.find((r) => totalDone < r.need);

  // Football cups — won only by finishing World Cup flags.
  const wcDone = worldCupDone(completed);
  const cupsEarned = FOOTBALL_CUPS.filter((c) => wcDone >= c.need).length;
  const nextCup = FOOTBALL_CUPS.find((c) => wcDone < c.need);

  return (
    <div className="facts-page trophy-page">
      <header className="player-header">
        <button className="home-btn" onClick={onHome} aria-label={t.backToHome}>
          {t.home}
        </button>
        <h1>{t.trophyTitle}</h1>
      </header>

      <p className="trophy-tally">
        {t.trophyTally(totalDone, totalAll)}{" "}
        {allDone ? t.trophyAllDone : t.trophyKeepGoing}
      </p>
      {!allDone && (
        <p className="trophy-hint">{t.trophyTapHint}</p>
      )}

      {allDone && (
        <div className="trophy-reset">
          {confirming ? (
            <div className="trophy-reset-confirm" role="alertdialog" aria-live="polite">
              <p>{t.trophyResetConfirm}</p>
              <div className="trophy-reset-actions">
                <button
                  className="trophy-reset-yes"
                  onClick={() => {
                    onReset();
                    setConfirming(false);
                  }}
                >
                  {t.trophyResetYes}
                </button>
                <button
                  className="trophy-reset-no"
                  onClick={() => setConfirming(false)}
                >
                  {t.trophyResetNo}
                </button>
              </div>
            </div>
          ) : (
            <button
              className="trophy-reset-btn"
              onClick={() => setConfirming(true)}
            >
              {t.trophyResetBtn}
            </button>
          )}
        </div>
      )}

      <section className="trophy-group reward-group">
        <div className="trophy-group-head">
          <h2>{t.trophyFriends}</h2>
          <span className="trophy-count">
            {rewardsEarned} / {rewards.length} 🏅
          </span>
        </div>
        <p className="reward-blurb">
          {nextReward
            ? t.trophyFriendsNext(
                nextReward.need - totalDone,
                rewardTextOf(nextReward.name, locale).article,
                rewardTextOf(nextReward.name, locale).name,
                nextReward.emoji,
              )
            : t.trophyFriendsAll}
        </p>
        {rewardsEarned > 0 && (
          <p className="reward-blurb reward-draw-hint">{t.trophyTapFriend}</p>
        )}
        <div className="reward-grid">
          {rewards.map((r) => {
            const earned = totalDone >= r.need;
            // `r.name` stays the English KEY (FRIEND_DRAWINGS is looked up by
            // it); only what the child reads is translated.
            const { name: label, article } = rewardTextOf(r.name, locale);
            const inner = (
              <>
                <span
                  className="reward-emoji"
                  aria-hidden="true"
                  style={{ fontSize: `${r.size}rem` }}
                >
                  {earned ? r.emoji : "❓"}
                </span>
                <span className="reward-name">
                  {earned ? label : t.trophyDrawingsToUnlock(r.need)}
                </span>
              </>
            );
            // Earned friends are tappable to draw them; locked ones are teasers.
            const drawing = FRIEND_DRAWINGS[r.name];
            return earned && drawing ? (
              <button
                className="reward earned"
                key={r.name}
                onClick={() => onPick(drawing)}
                aria-label={t.trophyDrawFriendAria(article, label)}
                title={t.trophyDrawFriendTitle(article, label)}
              >
                {inner}
              </button>
            ) : (
              <div
                className="reward locked"
                key={r.name}
                title={t.trophyFriendLocked(label, r.need)}
              >
                {inner}
              </div>
            );
          })}
        </div>
      </section>

      <section className="trophy-group reward-group">
        <div className="trophy-group-head">
          <h2>{t.trophyCups}</h2>
          <span className="trophy-count">
            {cupsEarned} / {FOOTBALL_CUPS.length} 🏆
          </span>
        </div>
        <p className="reward-blurb">
          {nextCup
            ? t.trophyCupsNext(
                nextCup.need - wcDone,
                cupTextOf(nextCup.name, locale).article,
                cupTextOf(nextCup.name, locale).name,
                nextCup.emoji,
              )
            : t.trophyCupsAll}
        </p>
        <div className="reward-grid">
          {FOOTBALL_CUPS.map((c, i) => {
            const earned = wcDone >= c.need;
            const cupName = cupTextOf(c.name, locale).name;
            return (
              <div
                className={earned ? "reward earned" : "reward locked"}
                key={c.name}
                title={earned ? cupName : t.trophyCupLocked(cupName, c.need)}
              >
                <span
                  className="reward-emoji"
                  aria-hidden="true"
                  style={{ fontSize: `${1.8 + i * 0.2}rem` }}
                >
                  {earned ? c.emoji : "❓"}
                </span>
                <span className="reward-name">
                  {earned ? cupName : t.trophyCupFlags(c.need)}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      {groups.map((g) => {
        const done = g.items.filter(({ a }) => g.done.has(a.id)).length;
        const pct = g.items.length ? Math.round((done / g.items.length) * 100) : 0;
        return (
          <section className="trophy-group" key={g.title}>
            <div className="trophy-group-head">
              <h2>{t[g.title]}</h2>
              <span className="trophy-count">
                {done} / {g.items.length} ⭐
              </span>
            </div>
            <div
              className="trophy-bar"
              role="progressbar"
              aria-valuenow={done}
              aria-valuemin={0}
              aria-valuemax={g.items.length}
            >
              <span className="trophy-bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="sticker-grid">
              {g.items.map(({ a, view }) => {
                const earned = g.done.has(a.id);
                const inner = (
                  <>
                    <DrawingThumb animal={view} className="sticker-art" />
                    <span className="sticker-name">
                      {view.emoji} {view.name}
                    </span>
                  </>
                );
                // Done ones are just shown; not-done ones are tappable to draw.
                return earned ? (
                  <div
                    className="sticker earned"
                    key={a.id}
                    title={t.trophyStickerDone(view.name)}
                  >
                    {inner}
                    <span className="sticker-check" aria-hidden="true">
                      ✓
                    </span>
                  </div>
                ) : (
                  <button
                    className="sticker locked"
                    key={a.id}
                    onClick={() => onPick(a)}
                    aria-label={t.trophyStickerLockedAria(
                      genderOf(a.id, locale),
                      view.name,
                    )}
                  >
                    {inner}
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
